import React from 'react';
import styles from './Disclosure.module.css';

const DisclosurePage = () => {
  return (
    <div className={styles.disclosureContainer}>
      <div className={styles.disclosureContent}>
        <div className={styles.importantNotice}>
          <h2>IMPORTANT DISCLOSURES & TERMS OF SERVICE</h2>
          <p className={styles.noticeText}>
            Please read these disclosures carefully before using our platform.
          </p>
        </div>

        {/* Platform Overview */}
        <section className={styles.disclosureSection}>
          <h3 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>1</span>
            Platform Overview & Purpose
          </h3>
          <div className={styles.sectionContent}>
            <h4>Problem Statement</h4>
            <p>
              Traditional informal lending among friends and family often leads to:
            </p>
            <ul className={styles.bulletList}>
              <li>Risk of relationship damage due to unpaid loans</li>
              <li>Lack of legal protection for lenders</li>
              <li>Absence of proper documentation</li>
              <li>Difficulty in tracking multiple loans</li>
              <li>No systematic reminder or recovery process</li>
            </ul>
            
            <h4>Our Solution</h4>
            <p>
              Our platform is a Friend-to-Friend (F2F) lending platform designed to:
            </p>
            <ul className={styles.bulletList}>
              <li>Provide legal documentation for informal loans</li>
              <li>Maintain transparency between lenders and borrowers</li>
              <li>Protect relationships through structured agreements</li>
              <li>Offer flexible repayment options</li>
              <li>Create social credit scoring among known networks</li>
            </ul>
          </div>
        </section>

        {/* Key Features */}
        <section className={styles.disclosureSection}>
          <h3 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>2</span>
            Platform Features
          </h3>
          <div className={styles.sectionContent}>
            <div className={styles.featuresGrid}>
              <div className={styles.featureCard}>
                <div className={styles.featureIcon}>🤝</div>
                <h5>Relationship Verification</h5>
                <p>Multi-layer verification to ensure genuine relationships</p>
              </div>
              
              <div className={styles.featureCard}>
                <div className={styles.featureIcon}>📝</div>
                <h5>Digital Agreements</h5>
                <p>Legally binding contracts with digital signatures</p>
              </div>
              
              <div className={styles.featureCard}>
                <div className={styles.featureIcon}>🔄</div>
                <h5>Flexible Repayment</h5>
                <p>EMI or lump-sum payment options</p>
              </div>
              
              <div className={styles.featureCard}>
                <div className={styles.featureIcon}>📊</div>
                <h5>Social Credit Score</h5>
                <p>Score based on repayment behavior</p>
              </div>
              
              <div className={styles.featureCard}>
                <div className={styles.featureIcon}>👥</div>
                <h5>Community Pods</h5>
                <p>Group lending for communities and organizations</p>
              </div>
              
              <div className={styles.featureCard}>
                <div className={styles.featureIcon}>🛡️</div>
                <h5>Recovery Support</h5>
                <p>Structured recovery process for defaults</p>
              </div>
            </div>
          </div>
        </section>

        {/* Relationship Verification */}
        <section className={styles.disclosureSection}>
          <h3 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>3</span>
            Relationship Verification Process
          </h3>
          <div className={styles.sectionContent}>
            <p className={styles.verificationNote}>
              <strong>Important:</strong> Our platform is strictly for lending between 
              known individuals. We verify relationships to prevent professional 
              lending and ensure compliance with state lending regulations.
            </p>
            
            <div className={styles.verificationMethods}>
              <h5>Verification Methods</h5>
              
              <div className={styles.methodCard}>
                <div className={styles.methodHeader}>
                  <span className={styles.methodNumber}>A</span>
                  <h6>Proximity Verification (Same/Local Area)</h6>
                </div>
                <ul>
                  <li>Address proof with matching PIN codes or within 20km radius</li>
                  <li>15-second verification video together stating relationship duration</li>
                  <li>AI-based video authenticity verification</li>
                  <li>Declaration: "Lending is not my business"</li>
                </ul>
              </div>
              
              <div className={styles.methodCard}>
                <div className={styles.methodHeader}>
                  <span className={styles.methodNumber}>B</span>
                  <h6>Educational/Professional Verification</h6>
                </div>
                <ul>
                  <li>School/College ID card verification</li>
                  <li>Office ID verification</li>
                  <li>AI-based document authenticity check</li>
                </ul>
              </div>
              
              <div className={styles.methodCard}>
                <div className={styles.methodHeader}>
                  <span className={styles.methodNumber}>C</span>
                  <h6>Co-living Verification</h6>
                </div>
                <ul>
                  <li>Rent agreement verification</li>
                  <li>PG/Hostel bill verification (last 3 months)</li>
                  <li>Payment screenshots as supplementary proof</li>
                </ul>
              </div>
              
              <div className={styles.methodCard}>
                <div className={styles.methodHeader}>
                  <span className={styles.methodNumber}>D</span>
                  <h6>Family Relationship Verification</h6>
                </div>
                <ul>
                  <li>Family tree verification through references</li>
                  <li>Reference video confirmation from family elders</li>
                  <li>ID verification for siblings (matching parent names)</li>
                </ul>
              </div>
              
              <p className={styles.agreementNote}>
                <strong>All users must sign:</strong> Mutual declaration of knowing each other 
                for minimum 12 months prior to agreement date.
              </p>
            </div>
          </div>
        </section>

        {/* Lending Limits & Restrictions */}
        <section className={styles.disclosureSection}>
          <h3 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>4</span>
            Lending Limits & Regulatory Compliance
          </h3>
          <div className={styles.sectionContent}>
            <div className={styles.limitsGrid}>
              <div className={`${styles.limitCard} ${styles.critical}`}>
                <h6>Annual Lending Limits</h6>
                <ul>
                  <li>Maximum 10 loans per year per lender</li>
                  <li>Maximum 15 lending-related transactions per year</li>
                  <li>Maximum 3 loans to same individual per year</li>
                  <li>At least 1 interest-free loan required per individual</li>
                </ul>
              </div>
              
              <div className={`${styles.limitCard} ${styles.critical}`}>
                <h6>Geographical Limits</h6>
                <ul>
                  <li>Maximum 3 loans to individuals within 20km radius</li>
                  <li>State-wise interest rate caps apply</li>
                </ul>
              </div>
              
              <div className={`${styles.limitCard} ${styles.critical}`}>
                <h6>Amount Limits</h6>
                <ul>
                  <li>Maximum ₹2,00,000 to single individual (across 3 loans)</li>
                  <li>Minimum loan amount: ₹1,000</li>
                  <li>Maximum loan amount: As per mutual agreement</li>
                </ul>
              </div>
              
              <div className={`${styles.limitCard} ${styles.critical}`}>
                <h6>Account Restrictions</h6>
                <ul>
                  <li>One account per individual (PAN & Aadhaar linked)</li>
                  <li>No duplicate accounts allowed</li>
                  <li>Monthly income verification may be required</li>
                </ul>
              </div>
            </div>
            
            <div className={styles.regulatoryNote}>
              <h5>Regulatory Compliance</h5>
              <p>
                Our platform operates as a technology platform facilitating loans between 
                known individuals. We are not a Non-Banking Financial Company (NBFC) 
                and do not engage in lending business. Users are responsible for 
                complying with:
              </p>
              <ul>
                <li>State money lending regulations (where applicable)</li>
                <li>Income Tax Act provisions for interest income</li>
                <li>GST regulations for platform fees</li>
                <li>Digital signature laws (IT Act, 2000)</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Fee Structure */}
        <section className={styles.disclosureSection}>
          <h3 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>5</span>
            Fee Structure & Charges
          </h3>
          <div className={styles.sectionContent}>
            <div className={styles.feeTable}>
              <table>
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Charges</th>
                    <th>Applicable When</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Platform Fee (Interest-free loans)</td>
                    <td>0%</td>
                    <td>Only during disbursement</td>
                  </tr>
                  <tr>
                    <td>Platform Fee (Interest-bearing loans)</td>
                    <td>5% of interest earned</td>
                    <td>Deducted during repayment</td>
                  </tr>
                  <tr>
                    <td>Community Pod Platform Fee</td>
                    <td>1.5% of contribution</td>
                    <td>During fund contribution</td>
                  </tr>
                  <tr>
                    <td>Pod Interest Fee</td>
                    <td>2% of interest charged</td>
                    <td>Deducted from pod interest</td>
                  </tr>
                  <tr>
                    <td>Payment Gateway Charges</td>
                    <td>As per bank/UPI rates</td>
                    <td>During all transactions</td>
                  </tr>
                  <tr>
                    <td>Late Payment Charges</td>
                    <td>As mutually agreed</td>
                    <td>After 3rd week of default</td>
                  </tr>
                  <tr>
                    <td>Legal/Recovery Fees</td>
                    <td>Actuals + 15% service fee</td>
                    <td>Only if recovery required</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className={styles.feeExample}>
              <h6>Example Calculation</h6>
              <div className={styles.exampleBox}>
                <p><strong>Loan Amount:</strong> ₹50,000</p>
                <p><strong>Interest Rate:</strong> 12% p.a.</p>
                <p><strong>Tenure:</strong> 1 year</p>
                <p><strong>Interest Earned:</strong> ₹6,000</p>
                <p><strong>Platform Fee (5% of interest):</strong> ₹300</p>
                <p><strong>Lender Receives:</strong> ₹55,700</p>
              </div>
            </div>
          </div>
        </section>

        {/* Recovery Process */}
        <section className={styles.disclosureSection}>
          <h3 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>6</span>
            Default & Recovery Process
          </h3>
          <div className={styles.sectionContent}>
            <div className={styles.recoveryTimeline}>
              <div className={styles.timelineStep}>
                <div className={styles.stepNumber}>1</div>
                <div className={styles.stepContent}>
                  <h6>Day 1-3: Grace Period</h6>
                  <p>Automatic reminders sent to borrower</p>
                </div>
              </div>
              
              <div className={styles.timelineStep}>
                <div className={styles.stepNumber}>2</div>
                <div className={styles.stepContent}>
                  <h6>Day 4-7: Extension Request</h6>
                  <p>Borrower can request extension (max 2 times)</p>
                </div>
              </div>
              
              <div className={styles.timelineStep}>
                <div className={styles.stepNumber}>3</div>
                <div className={styles.stepContent}>
                  <h6>Week 3: Late Charges</h6>
                  <p>Late charges applied (if agreed by lender)</p>
                </div>
              </div>
              
              <div className={`${styles.timelineStep} ${styles.warningStep}`}>
                <div className={styles.stepNumber}>4</div>
                <div className={styles.stepContent}>
                  <h6>After 2 Extensions: Score Impact</h6>
                  <p>Score downgraded • Yellow flag marked</p>
                </div>
              </div>
              
              <div className={`${styles.timelineStep} ${styles.warningStep}`}>
                <div className={styles.stepNumber}>5</div>
                <div className={styles.stepContent}>
                  <h6>Continued Default: Notices</h6>
                  <p>Legal notices issued • Red flag marked</p>
                </div>
              </div>
              
              <div className={`${styles.timelineStep} ${styles.criticalStep}`}>
                <div className={styles.stepNumber}>6</div>
                <div className={styles.stepContent}>
                  <h6>Final Stage: Recovery</h6>
                  <p>Third-party collection agents engaged • Legal action initiated</p>
                </div>
              </div>
            </div>
            
            <div className={styles.recoveryNotes}>
              <h6>Recovery Notes</h6>
              <ul>
                <li>Lender approval required before engaging recovery agents</li>
                <li>All recovery costs borne by defaulter</li>
                <li>Platform banned for 2 years for chronic defaulters</li>
                <li>Legal proceedings initiated under Indian Contract Act</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Social Credit System */}
        <section className={styles.disclosureSection}>
          <h3 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>7</span>
            Score & Social Credit
          </h3>
          <div className={styles.sectionContent}>
            <div className={styles.creditScoreInfo}>
              <div className={styles.scoreColors}>
                <div className={styles.colorIndicator}>
                  <div className={`${styles.colorBox} ${styles.green}`}></div>
                  <span>Green: Excellent (750-900)</span>
                </div>
                <div className={styles.colorIndicator}>
                  <div className={`${styles.colorBox} ${styles.blue}`}></div>
                  <span>Blue: Good (600-749)</span>
                </div>
                <div className={styles.colorIndicator}>
                  <div className={`${styles.colorBox} ${styles.yellow}`}></div>
                  <span>Yellow: Caution (450-599)</span>
                </div>
                <div className={styles.colorIndicator}>
                  <div className={`${styles.colorBox} ${styles.red}`}></div>
                  <span>Red: Restricted (0-449)</span>
                </div>
              </div>
              
              <div className={styles.scoreFactors}>
                <h6>Factors Affecting Score</h6>
                <ul>
                  <li>On-time repayment history (40%)</li>
                  <li>Relationship verification strength (20%)</li>
                  <li>Loan frequency and amounts (15%)</li>
                  <li>Community feedback (15%)</li>
                  <li>Document submission quality (10%)</li>
                </ul>
              </div>
            </div>
            
            <div className={styles.scoreImpact}>
              <h6>Score Impact & Restrictions</h6>
              <div className={styles.impactGrid}>
                <div className={styles.impactCard}>
                  <h7>Green Score</h7>
                  <p>Unrestricted access • Higher limits • Priority support</p>
                </div>
                <div className={styles.impactCard}>
                  <h7>Yellow Score</h7>
                  <p>Reduced limits • Additional verification required</p>
                </div>
                <div className={styles.impactCard}>
                  <h7>Red Score</h7>
                  <p>No lending for 1 year • Platform access restricted</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Legal Disclaimers */}
        <section className={`${styles.disclosureSection} ${styles.legalSection}`}>
          <h3 className={styles.sectionTitle}>
            <span className={styles.sectionNumber}>8</span>
            Legal Disclaimers & Risk Factors
          </h3>
          <div className={styles.sectionContent}>
            <div className={styles.disclaimerList}>
              <div className={styles.disclaimerItem}>
                <h6>Platform Role Disclaimer</h6>
                <p>
                  We act solely as a technology platform facilitating agreements 
                  between known parties. We are not a party to any loan agreement 
                  and do not guarantee repayment or assume credit risk.
                </p>
              </div>
              
              <div className={styles.disclaimerItem}>
                <h6>Lending Risk Disclaimer</h6>
                <p>
                  All lending carries risk of default. Platform features like social 
                  credit scoring and recovery support do not eliminate this risk. 
                  Lenders should only lend amounts they can afford to lose.
                </p>
              </div>
              
              <div className={styles.disclaimerItem}>
                <h6>Regulatory Compliance</h6>
                <p>
                  Users are responsible for complying with all applicable laws 
                  including but not limited to Income Tax Act, State Money Lending 
                  Acts, and GST regulations.
                </p>
              </div>
              
              <div className={styles.disclaimerItem}>
                <h6>Data Privacy & Security</h6>
                <p>
                  We implement bank-grade security (256-bit SSL encryption, ISO 27001 
                  compliance). However, users must protect their login credentials 
                  and report suspicious activity immediately.
                </p>
              </div>
              
              <div className={styles.disclaimerItem}>
                <h6>Dispute Resolution</h6>
                <p>
                  All disputes shall be subject to arbitration in accordance with 
                  Arbitration and Conciliation Act, 1996. Jurisdiction: Courts in 
                  [Your City], India.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DisclosurePage;